import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarBlank, CurrencyDollar, Plus, UserCircle, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DateField } from "@/components/DateField";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";

const today = () => format(new Date(), "yyyy-MM-dd");
const plusDays = (n) => format(new Date(Date.now() + n * 86400000), "yyyy-MM-dd");

const Col = ({ icon: Icon, title, children }) => (
  <div className="min-w-0 border-zinc-800 px-4 py-3 md:border-l md:first:border-l-0">
    <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
      <Icon size={14} weight="bold" className="text-indigo-300" /> {title}
    </div>
    {children}
  </div>
);

// Line-item invoice builder: bills whatever is left on the accepted quote, applies paid deposits.
export const InvoiceComposer = ({ open, onClose, project, onDone }) => {
  const [info, setInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState({ printed: false, internal: false });

  useEffect(() => {
    if (!open) return;
    setInfo(null);
    api
      .get(`/projects/${project.id}/invoice-lines`)
      .then(({ data }) => {
        setInfo(data);
        setRows(
          data.lines.map((l) => ({
            ...l,
            on: l.qty_left > 0,
            bill_qty: l.qty_left > 0 ? l.qty_left : 0,
            desc: "",
          })),
        );
        setForm({
          po_number: "",
          issue_date: today(),
          due_date: plusDays(data.due_days || 0),
          notes: "",
          internal_note: "",
          send: !!data.customer?.email,
          apply_credit: data.credit_available > 0 ? data.credit_available : 0,
        });
        setNotes({ printed: false, internal: false });
      })
      .catch((e) => toast.error(formatApiErrorDetail(e, "Could not load the quote lines")));
  }, [open, project.id]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setRow = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const subtotal = useMemo(
    () => rows.reduce((s, r) => (r.on ? s + Number(r.bill_qty || 0) * r.unit_price : s), 0),
    [rows],
  );
  const credit = Math.min(Number(form.apply_credit || 0), info?.credit_available || 0, subtotal);
  const balance = Math.max(0, subtotal - credit);
  const groups = useMemo(() => {
    const g = {};
    rows.forEach((r, i) => ((g[r.area || "Quote"] ||= []).push(i)));
    return g;
  }, [rows]);
  const allOn = rows.length > 0 && rows.every((r) => r.on);

  const submit = async () => {
    const lines = rows
      .filter((r) => r.on && Number(r.bill_qty) > 0)
      .map((r) => ({ key: r.key, item: r.item, description: r.desc, qty: Number(r.bill_qty), unit: r.unit, unit_price: r.unit_price, area: r.area }));
    if (!lines.length) {
      toast.error("Tick at least one line to bill");
      return;
    }
    if (balance <= 0) {
      toast.error("Nothing left to bill after the deposit credit");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/invoices`, {
        amount: balance, kind: "progress", quote_id: info.quote_id, title: "",
        due_date: form.due_date, issue_date: form.issue_date, notes: form.notes,
        internal_note: form.internal_note, po_number: form.po_number, send: form.send,
        lines, apply_credit: credit,
      });
      toast.success(`${data.invoice?.code || "Invoice"} issued for ${money(balance)}`, {
        description: data.emailed_to ? `Emailed to ${data.emailed_to}` : "Not emailed",
      });
      onDone?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Could not issue the invoice"));
    }
    setBusy(false);
  };

  const c = info?.customer || {};
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] w-[96vw] max-w-6xl overflow-y-auto border-zinc-800 bg-zinc-950 p-0" data-testid="invoice-composer">
        <DialogHeader className="flex-row flex-wrap items-center gap-3 border-b border-zinc-800 px-5 py-4">
          <DialogTitle className="text-lg">Issue invoice</DialogTitle>
          {info && (
            <span className="font-mono-data rounded border border-zinc-800 px-2 py-0.5 text-xs text-zinc-300" data-testid="invoice-composer-code">
              {info.next_code}
            </span>
          )}
          <label className="ml-auto flex items-center gap-2 text-xs text-zinc-400">
            Customer PO #
            <Input data-testid="invoice-composer-po" value={form.po_number || ""} onChange={(e) => set({ po_number: e.target.value })} className="h-8 w-44 border-zinc-800 bg-zinc-900" />
          </label>
        </DialogHeader>

        {!info ? (
          <div className="p-8 text-sm text-zinc-500">Loading quote lines…</div>
        ) : (
          <div className="p-5">
            <div className="grid rounded-lg border border-zinc-800 bg-zinc-900/50 md:grid-cols-4">
              <Col icon={UserCircle} title="Recipient">
                <div className="text-sm font-semibold text-zinc-100">{c.company || c.name || "—"}</div>
                {c.company && <div className="text-xs text-zinc-400">{c.name}</div>}
                <div className="mt-1 text-xs text-zinc-400">{c.phone}</div>
                <div className="text-xs text-zinc-400">{c.email || "no email on file"}</div>
                <div className="mt-2 text-xs text-zinc-500">Onsite: {c.address || "—"}</div>
              </Col>
              <Col icon={UserCircle} title="Issuer">
                <div className="text-sm font-semibold text-zinc-100">Brioni Granite</div>
                <div className="mt-1 text-xs text-zinc-500">Quote {info.quote_code || "—"}</div>
              </Col>
              <Col icon={CurrencyDollar} title="Payment info">
                <div className="text-xs text-zinc-500">Pricing tier</div>
                <div className="text-sm text-zinc-200">{c.tier}</div>
                <div className="mt-2 text-xs text-zinc-500">Payment terms</div>
                <div className="text-sm text-zinc-200">{info.terms || "—"}</div>
                <div className="mt-2 text-xs text-zinc-500">Sales tax</div>
                <div className="text-sm text-zinc-200">{info.tax_rate ? `${info.tax_rate}%` : "—"}</div>
              </Col>
              <Col icon={CalendarBlank} title="Dates">
                <div className="text-xs text-zinc-500">Issue date</div>
                <DateField testid="invoice-composer-issue-date" value={form.issue_date} onChange={(v) => set({ issue_date: v })} />
                <div className="mt-2 text-xs text-zinc-500">Due date</div>
                <DateField testid="invoice-composer-due-date" value={form.due_date} onChange={(v) => set({ due_date: v })} />
              </Col>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Notes</span>
              <div className="flex gap-3 text-xs">
                <button type="button" data-testid="invoice-composer-add-internal" onClick={() => setNotes((n) => ({ ...n, internal: !n.internal }))} className="flex items-center gap-1 text-zinc-300 hover:text-white">
                  <Plus size={12} weight="bold" /> Internal note
                </button>
                <button type="button" data-testid="invoice-composer-add-printed" onClick={() => setNotes((n) => ({ ...n, printed: !n.printed }))} className="flex items-center gap-1 text-zinc-300 hover:text-white">
                  <Plus size={12} weight="bold" /> Printed note
                </button>
              </div>
            </div>
            {notes.internal && <Textarea data-testid="invoice-composer-internal-note" placeholder="Internal note (office only)" value={form.internal_note} onChange={(e) => set({ internal_note: e.target.value })} className="mt-2 border-zinc-800 bg-zinc-900" />}
            {notes.printed && <Textarea data-testid="invoice-composer-printed-note" placeholder="Printed on the invoice" value={form.notes} onChange={(e) => set({ notes: e.target.value })} className="mt-2 border-zinc-800 bg-zinc-900" />}

            <div className="mt-3 overflow-x-auto rounded-lg border border-zinc-800">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead className="bg-zinc-900 text-[10px] uppercase tracking-widest text-zinc-400">
                  <tr>
                    <th className="w-8 px-3 py-2"><input type="checkbox" data-testid="invoice-composer-all" checked={allOn} onChange={(e) => setRows((r) => r.map((x) => ({ ...x, on: e.target.checked && x.qty_left > 0 })))} className="h-4 w-4 accent-indigo-500" /></th>
                    <th className="px-2 py-2">Item</th>
                    <th className="px-2 py-2">Description</th>
                    <th className="px-2 py-2 text-right">Qty orig.</th>
                    <th className="px-2 py-2 text-right">Qty left</th>
                    <th className="px-2 py-2 text-right">Qty</th>
                    <th className="px-2 py-2 text-right">Unit price</th>
                    <th className="px-3 py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groups).map(([area, idxs]) => (
                    <GroupRows key={area} area={area} idxs={idxs} rows={rows} setRow={setRow} />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex flex-col items-end gap-1 text-sm">
              <div className="text-zinc-400">Subtotal <span className="font-mono-data ml-4 text-zinc-100">{money(subtotal)}</span></div>
              {info.credit_available > 0 && (
                <label className="flex items-center gap-2 text-zinc-400" data-testid="invoice-composer-credit">
                  Use deposit funds (paid {money(info.credit_available)})
                  <span className="text-zinc-500">$</span>
                  <Input type="number" min="0" step="0.01" max={info.credit_available} value={form.apply_credit} onChange={(e) => set({ apply_credit: e.target.value })} className="h-8 w-28 border-zinc-800 bg-zinc-900 text-right" data-testid="invoice-composer-credit-input" />
                  <button type="button" onClick={() => set({ apply_credit: 0 })} className="text-red-400 hover:text-red-300"><X size={14} weight="bold" /></button>
                </label>
              )}
              <div className="text-base font-bold text-zinc-100" data-testid="invoice-composer-balance">Balance <span className="font-mono-data ml-4">{money(balance)}</span></div>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 pt-4">
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" data-testid="invoice-composer-send" checked={!!form.send} onChange={(e) => set({ send: e.target.checked })} className="h-4 w-4 accent-indigo-500" />
                Email to customer now{c.email ? "" : " (no email on file)"}
              </label>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose} className="text-zinc-300">Cancel</Button>
                <Button data-testid="invoice-composer-submit" disabled={busy} onClick={submit} className="bg-indigo-500 text-white hover:bg-indigo-400">
                  {busy ? "Issuing…" : `Issue invoice · ${money(balance)}`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const GroupRows = ({ area, idxs, rows, setRow }) => {
  const total = idxs.reduce((s, i) => (rows[i].on ? s + Number(rows[i].bill_qty || 0) * rows[i].unit_price : s), 0);
  return (
    <>
      <tr className="border-t border-zinc-800 bg-zinc-900/60">
        <td className="px-3 py-1.5" />
        <td colSpan={6} className="px-2 py-1.5 text-xs font-bold text-zinc-200">{area} <span className="font-normal text-zinc-500">· {idxs.length} items</span></td>
        <td className="font-mono-data px-3 py-1.5 text-right text-xs text-zinc-200">{money(total)}</td>
      </tr>
      {idxs.map((i) => {
        const r = rows[i];
        return (
          <tr key={r.key || i} className={`border-t border-zinc-800/70 ${r.on ? "" : "opacity-50"}`} data-testid={`invoice-composer-row-${i}`}>
            <td className="px-3 py-1.5"><input type="checkbox" checked={r.on} disabled={r.qty_left <= 0 && !r.on} onChange={(e) => setRow(i, { on: e.target.checked })} className="h-4 w-4 accent-indigo-500" /></td>
            <td className="px-2 py-1.5 text-zinc-100">{r.item}</td>
            <td className="px-2 py-1.5"><Input value={r.desc} placeholder={r.description} onChange={(e) => setRow(i, { desc: e.target.value })} className="h-8 border-zinc-800 bg-zinc-900 text-xs" /></td>
            <td className="font-mono-data px-2 py-1.5 text-right text-zinc-400">{r.qty} {r.unit}</td>
            <td className="font-mono-data px-2 py-1.5 text-right text-zinc-400">{r.qty_left} {r.unit}</td>
            <td className="px-2 py-1.5 text-right">
              <div className="ml-auto flex w-28 items-center rounded-md border border-zinc-800 bg-zinc-900">
                <input type="number" min="0" step="0.01" value={r.bill_qty} onChange={(e) => setRow(i, { bill_qty: e.target.value })} className="font-mono-data h-8 w-full bg-transparent px-2 text-right text-sm text-zinc-100 outline-none" data-testid={`invoice-composer-qty-${i}`} />
                <span className="pr-2 text-[10px] text-zinc-500">{r.unit}</span>
              </div>
            </td>
            <td className="font-mono-data px-2 py-1.5 text-right text-zinc-300">{money(r.unit_price)}</td>
            <td className="font-mono-data px-3 py-1.5 text-right text-zinc-100">{money(r.on ? Number(r.bill_qty || 0) * r.unit_price : 0)}</td>
          </tr>
        );
      })}
    </>
  );
};
